import React from 'react';
import { Activity, CheckCircle, AlertTriangle, Loader, AlertCircle } from 'lucide-react';
import './ConsistencySection.css';

const ConsistencySection = ({ data, loading, error }) => {
  // Safe number formatting helper
  const safeFixed = (value, decimals = 2) => {
    return (value != null && !isNaN(value)) ? Number(value).toFixed(decimals) : 'N/A';
  };

  if (loading) {
    return (
      <div className="section-loading">
        <div className="section-loading-spinner" />
        <p>Loading consistency analysis...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="section-error">
        <AlertCircle className="section-error-icon" />
        <p className="section-error-message">{error}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="section-no-data">
        <p>No consistency data available. Complete at least 3 attempts to see consistency analysis.</p>
      </div>
    );
  }

  // Format metric names for display
  const formatMetricName = (key) => {
    // Ensure key is a string
    if (typeof key !== 'string') {
      return String(key);
    }

    const nameMap = {
      jump_height_cm: 'Jump Height',
      rotation_speed_rev_s: 'Rotation Speed',
      rotation_count: 'Rotation Count',
      takeoff_velocity_m_s: 'Takeoff Velocity',
      landing_stability_score: 'Landing Stability',
      isu_score: 'ISU Score',
      air_time_s: 'Air Time'
    };
    return nameMap[key] || key.replace(/_/g, ' ');
  };

  // Get consistency score color
  const getConsistencyColor = (score) => {
    if (score >= 90) return '#10b981'; // Elite - Green
    if (score >= 80) return '#3b82f6'; // Competition Ready - Blue
    if (score >= 70) return '#8b5cf6'; // Good - Purple
    if (score >= 60) return '#f59e0b'; // Developing - Orange
    return '#ef4444'; // Needs Improvement - Red
  };

  // Get rating icon
  const getRatingIcon = (rating) => {
    if (rating === 'elite_level_consistency' || rating === 'competition_ready') {
      return <CheckCircle className="rating-icon rating-good" />;
    }
    return <AlertTriangle className="rating-icon rating-warning" />;
  };

  // Format rating for display
  const formatRating = (rating) => {
    if (!rating) return 'Unknown';
    // Ensure rating is a string
    if (typeof rating !== 'string') {
      return String(rating);
    }

    const ratingMap = {
      elite_level_consistency: 'Elite Level',
      competition_ready: 'Competition Ready',
      good_consistency: 'Good',
      developing_consistency: 'Developing',
      needs_improvement: 'Needs Improvement'
    };
    return ratingMap[rating] || rating.replace(/_/g, ' ');
  };

  const consistencyScore = data.consistency_score || 0;
  const consistencyColor = getConsistencyColor(consistencyScore);
  const metricBreakdown = data.metric_breakdown || {};
  const mostConsistent = data.most_consistent_metrics || [];
  const needsWork = data.needs_work_metrics || [];

  return (
    <div className="consistency-section">
      {/* Consistency Score Gauge */}
      <div className="consistency-gauge-container">
        <div className="consistency-gauge">
          <svg viewBox="0 0 200 120" className="gauge-svg">
            {/* Background arc */}
            <path
              d="M 20 100 A 80 80 0 0 1 180 100"
              fill="none"
              stroke="#e5e7eb"
              strokeWidth="20"
              strokeLinecap="round"
            />
            {/* Progress arc */}
            <path
              d="M 20 100 A 80 80 0 0 1 180 100"
              fill="none"
              stroke={consistencyColor}
              strokeWidth="20"
              strokeLinecap="round"
              strokeDasharray={`${(consistencyScore / 100) * 251.2} 251.2`}
              style={{ transition: 'stroke-dasharray 1s ease-in-out' }}
            />
          </svg>
          <div className="gauge-value" style={{ color: consistencyColor }}>
            {safeFixed(consistencyScore, 0)}
          </div>
          <div className="gauge-label">Consistency Score</div>
        </div>
      </div>

      {/* Rating Badge */}
      <div className="rating-badge">
        {getRatingIcon(data.rating)}
        <span className="rating-text">{formatRating(data.rating)}</span>
      </div>

      {/* Sample Size Info */}
      {data.sample_size && (
        <div className="sample-info">
          <span className="sample-text">
            Based on {data.sample_size} attempts over {data.date_range_days} days
          </span>
        </div>
      )}

      {/* Metric Breakdown Table */}
      {Object.keys(metricBreakdown).length > 0 && (
        <div className="metric-breakdown">
          <h5 className="breakdown-title">Metric Breakdown</h5>
          <div className="breakdown-table">
            <div className="breakdown-header">
              <div className="breakdown-cell">Metric</div>
              <div className="breakdown-cell">Score</div>
              <div className="breakdown-cell">Variance</div>
            </div>
            {Object.entries(metricBreakdown).map(([metric, breakdown]) => (
              <div key={metric} className="breakdown-row">
                <div className="breakdown-cell metric-name-cell">
                  {formatMetricName(metric)}
                </div>
                <div className="breakdown-cell score-cell">
                  <div
                    className="score-bar"
                    style={{
                      width: `${breakdown.score}%`,
                      backgroundColor: getConsistencyColor(breakdown.score)
                    }}
                  />
                  <span className="score-value">{safeFixed(breakdown.score, 0)}</span>
                </div>
                <div className="breakdown-cell variance-cell">
                  {safeFixed(breakdown.cv, 1)}%
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Most Consistent Metrics */}
      {mostConsistent.length > 0 && (
        <div className="consistent-metrics">
          <h5 className="metrics-subtitle">
            <CheckCircle className="subtitle-icon" style={{ color: '#10b981' }} />
            Most Consistent
          </h5>
          <div className="metrics-tags">
            {mostConsistent.map((item, index) => {
              // Handle both object format {metric: "...", metric_name: "..."} and string format
              const metricName = typeof item === 'object' && item !== null
                ? (item.metric_name || formatMetricName(item.metric))
                : formatMetricName(item);

              return (
                <span key={index} className="metric-tag consistent-tag">
                  {metricName}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Needs Work Metrics */}
      {needsWork.length > 0 && (
        <div className="needs-work-metrics">
          <h5 className="metrics-subtitle">
            <AlertTriangle className="subtitle-icon" style={{ color: '#f59e0b' }} />
            Needs Consistency Work
          </h5>
          <div className="metrics-tags">
            {needsWork.map((item, index) => {
              // Handle both object format {metric: "...", metric_name: "..."} and string format
              const metricName = typeof item === 'object' && item !== null
                ? (item.metric_name || formatMetricName(item.metric))
                : formatMetricName(item);

              return (
                <span key={index} className="metric-tag needs-work-tag">
                  {metricName}
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsistencySection;
